import React, { forwardRef } from 'react'
import { withTranslation } from 'react-i18next'
import * as UIcons from '@iconscout/react-unicons'
import dayjs from 'dayjs'
import ClickAwayComponent from 'components/ClickAwayComponent'
import RewardAdsStatusTag from '../RewardAdsStatusTag'

export default withTranslation()(function DetailHeader({ t, campaign, onEdit, onDuplicate, onEndCampaign }) {
  const fields = [
    { label: t('rewardAds.detail.budget'), value: `NGN ${campaign.budget.toLocaleString()}` },
    { label: t('rewardAds.detail.daily_budget'), value: `NGN ${campaign.dailyBudget.toLocaleString()}` },
    { label: t('rewardAds.detail.spent'), value: `NGN ${campaign.spent.toLocaleString()} (${campaign.spentPercent}%)` },
    { label: t('rewardAds.detail.period'), value: `${campaign.period.start} - ${campaign.period.end}` },
    { label: t('rewardAds.detail.merchant'), value: campaign.merchant }
  ]

  return (
    <div className="w-full bg-white rounded-2xl border border-border-lines p-4 mb-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-14 h-14 rounded-lg bg-${campaign.thumbnailTone} flex items-center justify-center shrink-0 overflow-hidden`}>
            {
              campaign.thumbnailUrl
                ? <img src={campaign.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                : <UIcons.UilPlayCircle size={26} className="text-white" />
            }
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-lg font-semibold text-dark-blue truncate">{campaign.title}</p>
              <RewardAdsStatusTag status={campaign.status} />
              {
                campaign.blocked && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-danger bg-opacity-10 text-danger">
                    <UIcons.UilBan size={12} /> {t('rewardAds.detail.blocked')}
                  </span>
                )
              }
            </div>
            <p className="text-sm text-secondary truncate">{campaign.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1.5 border border-border-lines rounded-full h-9 px-3.5 text-sm font-medium text-dark-blue whitespace-nowrap"
          >
            <UIcons.UilEditAlt size={15} /> {t('rewardAds.detail.edit')}
          </button>

          <ClickAwayComponent
            Component={forwardRef(({ handleClick, open }, ref) => (
              <div className="relative" ref={ref}>
                <button
                  type="button"
                  onClick={handleClick}
                  className="w-9 h-9 flex items-center justify-center rounded-full border border-border-lines"
                >
                  <UIcons.UilEllipsisV size={16} className="text-secondary" />
                </button>

                {
                  open && (
                    <div className="absolute right-0 top-11 z-20 w-48 bg-white border border-border-lines rounded-xl shadow-lg py-2">
                      <button
                        type="button"
                        onClick={() => { onDuplicate(campaign); handleClick() }}
                        className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-dark-blue hover:bg-background"
                      >
                        <UIcons.UilCopy size={16} /> {t('rewardAds.listing.actions.duplicate')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { onEndCampaign(campaign); handleClick() }}
                        className="w-full flex items-center gap-2 text-left px-4 py-2 text-sm text-danger hover:bg-background"
                      >
                        <UIcons.UilTimesSquare size={16} /> {t('rewardAds.listing.actions.end_campaign')}
                      </button>
                    </div>
                  )
                }
              </div>
            ))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-4 pt-4 border-t border-border-lines">
        {
          fields.map((field) => (
            <div key={field.label}>
              <p className="text-xs text-secondary uppercase tracking-wide">{field.label}</p>
              <p className="text-sm text-dark-blue mt-0.5">{field.value}</p>
            </div>
          ))
        }
      </div>

      {
        campaign.deleteRequestedAt && (
          <div className="flex items-center gap-2 bg-danger bg-opacity-10 rounded-xl px-3 py-2 mt-4">
            <UIcons.UilExclamationTriangle size={16} className="text-danger shrink-0" />
            <p className="text-sm text-danger">
              {t('rewardAds.detail.deletion_requested', { date: dayjs(campaign.deleteRequestedAt).format('MMM D, YYYY h:mm A') })}
            </p>
          </div>
        )
      }
    </div>
  )
})
